import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { formatBC } from "@/lib/format";
import { toast } from "sonner";
import {
  Loader2, Users, Activity, TrendingUp, Sparkles, RefreshCw, Flame, UserPlus, Radio, Wallet, PieChart, RotateCcw,
} from "lucide-react";

function Metric({ icon: Icon, label, value, sub, testid, accent = "gold" }) {
  const accentClass = {
    gold: "text-[#FBBF24] border-[#FBBF24]/30",
    red: "text-[#DC2626] border-[#DC2626]/40",
    emerald: "text-emerald-300 border-emerald-400/30",
    blue: "text-[#93C5FD] border-[#1E3A8A]/40",
  }[accent] || "text-[#FBBF24] border-[#FBBF24]/30";
  return (
    <div className={`glass-card p-4 border ${accentClass.split(" ")[1]}`} data-testid={testid}>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-white/45 font-bold">
        <span>{label}</span>
        {Icon && <Icon size={13} className={accentClass.split(" ")[0]} />}
      </div>
      <div className={`font-mono-num text-3xl font-black mt-1 ${accentClass.split(" ")[0]}`}>{value}</div>
      {sub && <div className="text-[11px] text-white/50 mt-0.5">{sub}</div>}
    </div>
  );
}

/**
 * Compact SVG bar chart. Renders vertical bars for each bucket.
 * data: [{label, value}]
 */
function BarChart({ data, height = 140, maxValue, testid, accent = "#FBBF24" }) {
  const max = Math.max(1, maxValue ?? Math.max(...data.map((d) => d.value || 0)));
  const barW = 100 / Math.max(1, data.length);
  return (
    <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" className="w-full" style={{ height }} data-testid={testid}>
      {/* grid lines */}
      {[0.25, 0.5, 0.75].map((t) => (
        <line key={`gridline-${t}`} x1="0" y1={height * (1 - t)} x2="100" y2={height * (1 - t)} stroke="rgba(255,255,255,0.06)" strokeWidth="0.3" />
      ))}
      {data.map((d, i) => {
        const h = ((d.value || 0) / max) * (height - 12);
        const y = height - h;
        return (
          <g key={d.label ?? `bar-${i}`}>
            <rect
              x={i * barW + barW * 0.12}
              y={y}
              width={barW * 0.76}
              height={h > 0 ? h : 0.5}
              fill={accent}
              opacity={d.value > 0 ? 0.9 : 0.25}
              rx={0.4}
            >
              <title>{`${d.label}: ${d.value}`}</title>
            </rect>
          </g>
        );
      })}
    </svg>
  );
}

function AxisLabels({ data, everyN = 1 }) {
  return (
    <div className="grid text-[9px] text-white/40 mt-1 font-mono-num" style={{ gridTemplateColumns: `repeat(${data.length}, 1fr)` }}>
      {data.map((d, i) => (
        <div key={d.label ?? `axis-${i}`} className="text-center truncate" title={d.label}>
          {i % everyN === 0 ? d.label : ""}
        </div>
      ))}
    </div>
  );
}

/**
 * Executive Net Earnings block. Shows revenue, net profit and margin per
 * product. Backfill button + raw cost columns render only when
 * `data.cost_visible === true`, i.e. the caller is the super-admin.
 * Sub-admins with 'analytics' scope see revenue + net profit but NEVER see
 * capital cost — that visibility gate is enforced server-side too.
 */
function NetEarningsBlock() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/analytics/net-earnings");
      setData(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const backfill = async () => {
    if (!window.confirm("Retro-fill net profit on every historical order using each product's current cost? Orders already snapshotted stay untouched.")) return;
    setBackfilling(true);
    try {
      const { data: r } = await api.post("/admin/analytics/net-earnings/backfill");
      toast.success(`Backfilled ${r.updated} order${r.updated === 1 ? "" : "s"} (skipped ${r.skipped} already snapshotted).`);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBackfilling(false); }
  };

  if (loading || !data) {
    return (
      <div className="glass-card p-5 text-white/50 flex items-center gap-2" data-testid="net-earnings-loading">
        <Loader2 size={16} className="animate-spin" /> Loading net-earnings report…
      </div>
    );
  }

  const s = data.summary;
  const costVisible = !!data.cost_visible;
  const pct = (x) => `${(Number(x || 0) * 100).toFixed(1)}%`;

  return (
    <div className="space-y-4" data-testid="net-earnings">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">
          <PieChart size={12} /> Executive · Product Net Earnings
        </div>
        <div className="flex items-center gap-2">
          {costVisible && (
            <button
              onClick={backfill}
              disabled={backfilling}
              className="text-xs px-3 py-2 border border-white/15 rounded hover:border-[#FBBF24] hover:text-[#FBBF24] inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="net-earnings-backfill"
              title="Snapshot historical orders' net profit using each product's current cost"
            >
              <RotateCcw size={12} className={backfilling ? "animate-spin" : ""} />
              {backfilling ? "Backfilling…" : "Backfill historical"}
            </button>
          )}
          <button
            onClick={load}
            className="text-xs px-3 py-2 border border-white/15 rounded hover:border-[#FBBF24] hover:text-[#FBBF24] inline-flex items-center gap-1"
            data-testid="net-earnings-refresh"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="net-earnings-summary">
        <Metric icon={Wallet} label="Gross Revenue" value={`₱${formatBC(s.revenue_bc)}`} sub={`${s.units_sold} unit${s.units_sold === 1 ? "" : "s"} · ${s.orders_counted} order${s.orders_counted === 1 ? "" : "s"}`} accent="blue" testid="metric-revenue" />
        <Metric icon={TrendingUp} label="Net Earnings" value={`₱${formatBC(s.net_profit_bc)}`} sub={`After capital cost · avg ${pct(s.avg_margin_pct)} margin`} accent="emerald" testid="metric-net-profit" />
        <Metric icon={PieChart} label="Avg Margin" value={pct(s.avg_margin_pct)} sub="Portfolio-weighted" accent="gold" testid="metric-avg-margin" />
        {costVisible && (
          <Metric icon={Wallet} label="Capital Cost" value={`₱${formatBC(s.cost_bc)}`} sub="Super-admin view only" accent="red" testid="metric-cost" />
        )}
      </div>

      <div className="glass-card p-0 overflow-hidden" data-testid="net-earnings-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-[10px] uppercase tracking-[0.18em] text-white/50">
              <tr>
                <th className="text-left px-4 py-2.5">Product</th>
                <th className="text-right px-4 py-2.5">Units</th>
                <th className="text-right px-4 py-2.5">Revenue</th>
                {costVisible && <th className="text-right px-4 py-2.5 text-[#FCA5A5]">Cost</th>}
                <th className="text-right px-4 py-2.5">Net Earnings</th>
                <th className="text-right px-4 py-2.5">Margin</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {data.products.length === 0 ? (
                <tr>
                  <td colSpan={costVisible ? 6 : 5} className="px-4 py-8 text-center text-white/40" data-testid="net-earnings-empty">
                    No completed orders yet. Net earnings will populate as customers check out.
                  </td>
                </tr>
              ) : (
                data.products.map((p) => (
                  <tr key={p.product_id} className="hover:bg-white/[0.03]" data-testid={`net-row-${p.product_id}`}>
                    <td className="px-4 py-2.5 text-white">{p.product_name}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num text-white/80">{p.units_sold}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num text-[#93C5FD]">₱{formatBC(p.revenue_bc)}</td>
                    {costVisible && (
                      <td className="px-4 py-2.5 text-right font-mono-num text-[#FCA5A5]">₱{formatBC(p.cost_bc)}</td>
                    )}
                    <td className="px-4 py-2.5 text-right font-mono-num text-emerald-300 font-bold">₱{formatBC(p.net_profit_bc)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num text-[#FBBF24]">{pct(p.margin_pct)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-[10px] uppercase tracking-[0.22em] text-white/30 text-center">
        {costVisible
          ? "Super-admin view · capital cost columns are confidential"
          : "General view · capital cost hidden by policy"}
        {" · "}Cancelled orders excluded · Shipping fees excluded from margin
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [days, setDays] = useState(14);
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/analytics/activity`, { params: { days } });
      setState(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [days]);

  if (loading || !state) {
    return (
      <div className="text-white/50 flex items-center gap-2 p-6" data-testid="analytics-loading">
        <Loader2 size={16} className="animate-spin" /> Crunching activity data…
      </div>
    );
  }

  const h = state.headline;
  const hourly = state.hourly_24h.map((b) => ({ label: b.label, value: b.active_users }));
  const daily = state.daily.map((b) => ({ label: b.date.slice(5), value: b.active_users }));
  const signups = state.signups_daily.map((b) => ({ label: b.date.slice(5), value: b.signups }));

  return (
    <div className="space-y-5" data-testid="admin-analytics">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">
            <Sparkles size={12} /> Investor-Grade Activity Analytics
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight mt-1">
            Active Operators · Hourly &amp; Daily
          </h2>
          <p className="text-white/55 text-sm mt-1 max-w-2xl">
            Real users who pinged the platform in the last 24 hours (heartbeat every ~30s) and the last {days} days. Peaks
            marked, signup curve overlaid — screenshot-ready for your investor deck.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="zfd-input w-32 text-xs"
            data-testid="analytics-window-select"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
          <button onClick={load} className="text-xs px-3 py-2 border border-white/15 rounded hover:border-[#FBBF24] hover:text-[#FBBF24] inline-flex items-center gap-1" data-testid="analytics-refresh">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {/* Headline metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="analytics-headline">
        <Metric icon={Radio} label="Live Now" value={h.live_now} sub="last 5 min heartbeat" accent="red" testid="metric-live-now" />
        <Metric icon={Activity} label="DAU Today" value={h.dau_today} sub={`vs ${h.dau_yesterday} yesterday`} accent="gold" testid="metric-dau-today" />
        <Metric icon={Users} label="24h Active" value={h.active_last_24h} sub="rolling window" accent="blue" testid="metric-active-24h" />
        <Metric icon={Users} label="7-day Active" value={h.active_last_7d} sub="unique operators" accent="emerald" testid="metric-active-7d" />
        <Metric icon={TrendingUp} label="Total Operators" value={h.total_users} sub="all-time" accent="gold" testid="metric-total-users" />
        <Metric
          icon={Flame}
          label="Peak Hour"
          value={h.peak_hour ? h.peak_hour.active_users : 0}
          sub={h.peak_hour ? `at ${h.peak_hour.label}` : "—"}
          accent="red"
          testid="metric-peak-hour"
        />
      </div>

      {/* Executive Net Earnings — investor-ready P&L block. Cost columns are
          gated server-side so sub-admins see only revenue + net earnings. */}
      <NetEarningsBlock />

      {/* Hourly chart */}
      <div className="glass-card p-5" data-testid="chart-hourly">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">
            <Activity size={12} /> Hourly Active Users · last 24 h (UTC)
          </div>
          <div className="text-[11px] text-white/40">Peak: <strong className="text-white/70 font-mono-num">{h.peak_hour ? h.peak_hour.active_users : 0}</strong></div>
        </div>
        <BarChart data={hourly} accent="#FBBF24" testid="chart-hourly-bars" />
        <AxisLabels data={hourly} everyN={3} />
      </div>

      {/* Daily chart */}
      <div className="glass-card p-5" data-testid="chart-daily">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">
            <Users size={12} /> Daily Active Users · last {days} days
          </div>
          <div className="text-[11px] text-white/40">Peak: <strong className="text-white/70 font-mono-num">{h.peak_day ? h.peak_day.active_users : 0}</strong> on <span className="text-white/70">{h.peak_day?.date || "—"}</span></div>
        </div>
        <BarChart data={daily} accent="#DC2626" testid="chart-daily-bars" />
        <AxisLabels data={daily} everyN={Math.max(1, Math.round(daily.length / 10))} />
      </div>

      {/* Signup chart */}
      <div className="glass-card p-5" data-testid="chart-signups">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">
            <UserPlus size={12} /> New Operator Signups · last {days} days
          </div>
          <div className="text-[11px] text-white/40">Total: <strong className="text-white/70 font-mono-num">{signups.reduce((a, b) => a + b.value, 0)}</strong></div>
        </div>
        <BarChart data={signups} accent="#10B981" testid="chart-signups-bars" />
        <AxisLabels data={signups} everyN={Math.max(1, Math.round(signups.length / 10))} />
      </div>

      <div className="text-[10px] uppercase tracking-[0.22em] text-white/30 text-center pt-2">
        Data source: authenticated /auth/me and /me/heartbeat hits. UTC-hour buckets. Anti-bot: only signed-in sessions counted.
      </div>
    </div>
  );
}
